import type {Document} from '@contentful/rich-text-types'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {SectionIntroStacked} from '@primer/react-brand/esm'
import type {ComponentProps} from 'react'
import type {IconItem, Link, TextItem} from '../../types'
import {renderWithoutParagraphs} from '../../utils'

type FlexSuiteIntroSimpleProps = {
  heading?: Document
  description?: Document
  link?: Link
  items?: Array<IconItem | TextItem>
  promoteFirstHeading?: boolean
  variant?: ComponentProps<typeof SectionIntroStacked>['variant']
  headingSize?: ComponentProps<typeof SectionIntroStacked.Heading>['size']
  iconSize?: ComponentProps<typeof SectionIntroStacked.ItemIcon>['size']
}

export function FlexSuiteIntroSimple({
  heading,
  description,
  link,
  items,
  promoteFirstHeading = false,
  variant,
  headingSize,
  iconSize,
}: FlexSuiteIntroSimpleProps) {
  if (!heading && !description) return null

  const firstItemHeadingIndex = items?.findIndex(item => Boolean(item.fields.heading)) ?? -1

  return (
    <SectionIntroStacked variant={variant}>
      {heading ? (
        <SectionIntroStacked.Heading size={headingSize}>{renderWithoutParagraphs(heading)}</SectionIntroStacked.Heading>
      ) : null}
      {description ? (
        <SectionIntroStacked.Description>{renderWithoutParagraphs(description)}</SectionIntroStacked.Description>
      ) : null}
      {link?.fields.href ? (
        <SectionIntroStacked.Link href={link.fields.href}>{link.fields.text}</SectionIntroStacked.Link>
      ) : null}
      {items?.length ? (
        <SectionIntroStacked.Items>
          {items.map((item, index) => {
            const isIconItem = item.sys.contentType.sys.id === 'iconItem'
            const IconComponent = isIconItem && 'icon' in item.fields ? getPrimerIcon(item.fields.icon) : null

            return (
              <SectionIntroStacked.Item key={item.sys.id}>
                {IconComponent ? <SectionIntroStacked.ItemIcon icon={<IconComponent />} size={iconSize} /> : null}
                {item.fields.heading ? (
                  <SectionIntroStacked.ItemHeading
                    as={index === firstItemHeadingIndex && promoteFirstHeading ? 'h2' : 'h3'}
                    size="subhead-large"
                  >
                    {renderWithoutParagraphs(item.fields.heading)}
                  </SectionIntroStacked.ItemHeading>
                ) : null}
                {item.fields.description ? (
                  <SectionIntroStacked.ItemDescription>
                    {renderWithoutParagraphs(item.fields.description)}
                  </SectionIntroStacked.ItemDescription>
                ) : null}
              </SectionIntroStacked.Item>
            )
          })}
        </SectionIntroStacked.Items>
      ) : null}
    </SectionIntroStacked>
  )
}
